import React, { createContext, useContext, useEffect } from 'react'

import { type RequestStatus } from 'core/domain/Request'
import { useAsync } from 'core/ui/hooks/useAsync'

import { defaultConfigValues, type RemoteConfigType } from '../domain/Config'
import { fetchRemoteConfigs } from '../use-cases/fetchRemoteConfigs'

export type ConfigStatus = {
  status: RequestStatus
  isPending: boolean
  isDone: boolean
  hasError: boolean
  error?: unknown
}

export type ConfigContextValue = RemoteConfigType & { configStatus: ConfigStatus }

export const ConfigContext = createContext<ConfigContextValue | undefined>(undefined)
ConfigContext.displayName = 'ConfigContext'

export function ConfigProvider({ children }: { children?: React.ReactNode }) {
  const { call, result, status, error, isPending, isDone, hasError } = useAsync(fetchRemoteConfigs)
  const configs: RemoteConfigType = result ?? defaultConfigValues

  useEffect(
    function onMount() {
      call()
    },
    [call]
  )

  const value: ConfigContextValue = {
    ...configs,
    configStatus: {
      status,
      isPending,
      isDone,
      hasError,
      error,
    },
  }

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
}

export function useConfigContext() {
  const context = useContext(ConfigContext)

  if (context === undefined) {
    throw new Error(`useConfigContext must be used within a ConfigProvider`)
  }

  return context
}
